"use client";

import { c as _c } from "react/compiler-runtime";
import RenderImage from "@/components/atoms/RenderImage";
import { cn } from "@/lib/utils";
import { Images } from "lucide-react";
import type { ReactNode } from "react";
import { useHeroImageGrid } from "./useHeroImageGrid";
export type HeroImageGridVariant = "compact" | "expanded";
type Props = {
  pictures: string[];
  progressive?: boolean;
  variant?: HeroImageGridVariant;
  morePhotosLabel?: (count: number) => ReactNode;
  showCountBadge?: boolean;
  onClick?: () => void;
  onTileClick?: (index: number) => void;
  className?: string;
  "data-testid"?: string;
  altPrefix?: string;
  imagePriority?: boolean;
  imageQuality?: number;
  imageSizes?: {
    primary?: string;
    secondary?: string;
  };
  emptySlotPlaceholder?: boolean;
};
const tileClass = "relative rounded-lg overflow-hidden";
const overlayClass = "absolute inset-0 bg-black/60 flex items-center justify-center text-white font-semibold";
export default function HeroImageGrid(t0) {
  const $ = _c(91);
  const {
    pictures,
    progressive: t1,
    variant: t2,
    morePhotosLabel,
    showCountBadge: t3,
    onClick,
    onTileClick,
    className,
    "data-testid": dataTestId,
    altPrefix: t4,
    imagePriority,
    imageQuality,
    imageSizes,
    emptySlotPlaceholder: t5
  } = t0;
  const progressive = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "compact" : t2;
  const showCountBadge = t3 === undefined ? false : t3;
  const altPrefix = t4 === undefined ? "" : t4;
  const emptySlotPlaceholder = t5 === undefined ? true : t5;
  let t6;
  if ($[0] !== onClick || $[1] !== onTileClick || $[2] !== pictures || $[3] !== variant) {
    t6 = {
      pictures,
      variant,
      onClick,
      onTileClick
    };
    $[0] = onClick;
    $[1] = onTileClick;
    $[2] = pictures;
    $[3] = variant;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const {
    totalPhotos,
    morePhotos,
    isSingle,
    onlyTwo,
    tileClickFor,
    containerOnClick,
    tileCursor: cursor,
    containerCursor
  } = useHeroImageGrid(t6);
  if (variant === "expanded") {
    let t7;
    if ($[5] !== altPrefix || $[6] !== className || $[7] !== containerCursor || $[8] !== containerOnClick || $[9] !== cursor || $[10] !== dataTestId || $[11] !== imagePriority || $[12] !== imageQuality || $[13] !== imageSizes?.primary || $[14] !== imageSizes?.secondary || $[15] !== isSingle || $[16] !== morePhotos || $[17] !== morePhotosLabel || $[18] !== pictures || $[19] !== progressive || $[20] !== showCountBadge || $[21] !== tileClickFor || $[22] !== totalPhotos) {
      const rightTiles = pictures.slice(1, 5);
      let t8;
      if ($[24] !== className || $[25] !== containerCursor) {
        t8 = cn("grid grid-cols-6 grid-rows-2 h-96 gap-1", containerCursor, className);
        $[24] = className;
        $[25] = containerCursor;
        $[26] = t8;
      } else {
        t8 = $[26];
      }
      let t9;
      if ($[27] !== tileClickFor) {
        t9 = tileClickFor(0);
        $[27] = tileClickFor;
        $[28] = t9;
      } else {
        t9 = $[28];
      }
      const t10 = isSingle ? "col-span-6" : "col-span-4";
      let t11;
      if ($[29] !== cursor || $[30] !== t10) {
        t11 = cn(tileClass, cursor, "row-span-2", t10);
        $[29] = cursor;
        $[30] = t10;
        $[31] = t11;
      } else {
        t11 = $[31];
      }
      const t12 = imageSizes?.primary;
      let t13;
      if ($[32] !== altPrefix || $[33] !== imagePriority || $[34] !== imageQuality || $[35] !== pictures[0] || $[36] !== progressive || $[37] !== t12) {
        t13 = <RenderImage src={pictures[0]} alt={altPrefix} className="object-cover" priority={imagePriority} quality={imageQuality} sizes={t12} fadeIn={progressive} />;
        $[32] = altPrefix;
        $[33] = imagePriority;
        $[34] = imageQuality;
        $[35] = pictures[0];
        $[36] = progressive;
        $[37] = t12;
        $[38] = t13;
      } else {
        t13 = $[38];
      }
      let t14;
      if ($[39] !== showCountBadge || $[40] !== totalPhotos) {
        t14 = showCountBadge && totalPhotos > 1 && <div className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-xs text-white"><Images className="w-3.5 h-3.5" /><span className="font-medium">{totalPhotos}</span></div>;
        $[39] = showCountBadge;
        $[40] = totalPhotos;
        $[41] = t14;
      } else {
        t14 = $[41];
      }
      let t15;
      if ($[42] !== t11 || $[43] !== t13 || $[44] !== t14 || $[45] !== t9) {
        t15 = <div onClick={t9} className={t11}>{t13}{t14}</div>;
        $[42] = t11;
        $[43] = t13;
        $[44] = t14;
        $[45] = t9;
        $[46] = t15;
      } else {
        t15 = $[46];
      }
      t7 = <div onClick={containerOnClick} data-testid={dataTestId} className={t8}>{t15}{rightTiles.map((thumbnail, i) => {
          const isLastVisible = i === rightTiles.length - 1;
          const showOverflow = isLastVisible && morePhotos > 0;
          return <div key={i} onClick={tileClickFor(i + 1)} className={cn(tileClass, cursor, "col-span-2")}><RenderImage src={thumbnail} alt={altPrefix ? `${altPrefix} ${i + 1}` : ""} className="object-cover" quality={imageQuality} sizes={imageSizes?.secondary} fadeIn={progressive} />{showOverflow && morePhotosLabel && <div className={cn(overlayClass, "text-lg")}>{morePhotosLabel(morePhotos)}</div>}</div>;
        })}</div>;
      $[5] = altPrefix;
      $[6] = className;
      $[7] = containerCursor;
      $[8] = containerOnClick;
      $[9] = cursor;
      $[10] = dataTestId;
      $[11] = imagePriority;
      $[12] = imageQuality;
      $[13] = imageSizes?.primary;
      $[14] = imageSizes?.secondary;
      $[15] = isSingle;
      $[16] = morePhotos;
      $[17] = morePhotosLabel;
      $[18] = pictures;
      $[19] = progressive;
      $[20] = showCountBadge;
      $[21] = tileClickFor;
      $[22] = totalPhotos;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    return t7;
  }
  const t7 = isSingle ? "grid-cols-1" : "grid-cols-[2fr_1fr]";
  let t8;
  if ($[47] !== className || $[48] !== containerCursor || $[49] !== t7) {
    t8 = cn("relative grid grid-rows-2 gap-1 w-full aspect-[5/2]", t7, containerCursor, className);
    $[47] = className;
    $[48] = containerCursor;
    $[49] = t7;
    $[50] = t8;
  } else {
    t8 = $[50];
  }
  let t9;
  if ($[51] !== tileClickFor) {
    t9 = tileClickFor(0);
    $[51] = tileClickFor;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  const t10 = emptySlotPlaceholder && !pictures[0] && "border border-dashed border-border";
  let t11;
  if ($[53] !== cursor || $[54] !== t10) {
    t11 = cn(tileClass, cursor, "row-span-2 col-span-1", t10);
    $[53] = cursor;
    $[54] = t10;
    $[55] = t11;
  } else {
    t11 = $[55];
  }
  let t12;
  if ($[56] !== altPrefix || $[57] !== pictures[0] || $[58] !== progressive) {
    t12 = <RenderImage src={pictures[0]} alt={altPrefix} className="object-cover w-full h-full" fadeIn={progressive} data-sentry-element="RenderImage" data-sentry-source-file="HeroImageGrid.tsx" />;
    $[56] = altPrefix;
    $[57] = pictures[0];
    $[58] = progressive;
    $[59] = t12;
  } else {
    t12 = $[59];
  }
  let t13;
  if ($[60] !== t11 || $[61] !== t12 || $[62] !== t9) {
    t13 = <div onClick={t9} className={t11}>{t12}</div>;
    $[60] = t11;
    $[61] = t12;
    $[62] = t9;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== altPrefix || $[65] !== cursor || $[66] !== emptySlotPlaceholder || $[67] !== isSingle || $[68] !== onlyTwo || $[69] !== pictures[1] || $[70] !== progressive || $[71] !== tileClickFor) {
    t14 = !isSingle && <div onClick={tileClickFor(1)} className={cn(tileClass, cursor, onlyTwo && "row-span-2", emptySlotPlaceholder && !pictures[1] && "border border-dashed border-border")}><RenderImage src={pictures[1]} alt={altPrefix} className="object-cover w-full h-full" fadeIn={progressive} /></div>;
    $[64] = altPrefix;
    $[65] = cursor;
    $[66] = emptySlotPlaceholder;
    $[67] = isSingle;
    $[68] = onlyTwo;
    $[69] = pictures[1];
    $[70] = progressive;
    $[71] = tileClickFor;
    $[72] = t14;
  } else {
    t14 = $[72];
  }
  let t15;
  if ($[73] !== altPrefix || $[74] !== cursor || $[75] !== emptySlotPlaceholder || $[76] !== isSingle || $[77] !== morePhotos || $[78] !== morePhotosLabel || $[79] !== onlyTwo || $[80] !== pictures[2] || $[81] !== progressive || $[82] !== tileClickFor) {
    t15 = !isSingle && !onlyTwo && <div onClick={tileClickFor(2)} className={cn(tileClass, cursor, emptySlotPlaceholder && !pictures[2] && "border border-dashed border-border")}><RenderImage src={pictures[2]} alt={altPrefix} className="object-cover w-full h-full" fadeIn={progressive} />{morePhotos > 0 && morePhotosLabel && <div className={cn(overlayClass, "text-xs sm:text-sm")}>{morePhotosLabel(morePhotos)}</div>}</div>;
    $[73] = altPrefix;
    $[74] = cursor;
    $[75] = emptySlotPlaceholder;
    $[76] = isSingle;
    $[77] = morePhotos;
    $[78] = morePhotosLabel;
    $[79] = onlyTwo;
    $[80] = pictures[2];
    $[81] = progressive;
    $[82] = tileClickFor;
    $[83] = t15;
  } else {
    t15 = $[83];
  }
  let t16;
  if ($[84] !== containerOnClick || $[85] !== dataTestId || $[86] !== t13 || $[87] !== t14 || $[88] !== t15 || $[89] !== t8) {
    t16 = <div onClick={containerOnClick} data-testid={dataTestId} className={t8} data-sentry-component="HeroImageGrid" data-sentry-source-file="HeroImageGrid.tsx">{t13}{t14}{t15}</div>;
    $[84] = containerOnClick;
    $[85] = dataTestId;
    $[86] = t13;
    $[87] = t14;
    $[88] = t15;
    $[89] = t8;
    $[90] = t16;
  } else {
    t16 = $[90];
  }
  return t16;
}